import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BrandGroup as BrandGroupType,
  TopCategoriesModule
} from 'types/capi/modules';
import type { ExternalLink, InternalLink } from 'types/capi/shared';

import HorizontalLine from '~/components/HorizontalLine';
import Tracked from '~/components/Tracked';

type Props = {
  categories: TopCategoriesModule;
};

export default function TopCategories({ categories }: Props) {
  const { t } = useTranslation('home');

  return (
    <div className="w-full bg-white">
      <div className="flex max-w-desktop flex-col items-center gap-6 py-10 max-md:px-4 md:mx-auto md:py-20">
        <div className="flex flex-col items-center gap-6 self-stretch md:overflow-hidden">
          <h2 className=" text-center text-[32px] leading-10 md:text-[40px] md:leading-[48px]">
            {t('topCategories')}
          </h2>
          <p className="max-w-xl text-justify">{categories.text}</p>
          <div className="mt-7 flex w-full flex-col items-center gap-4 md:gap-6">
            <BrandGroups brands={categories.brand_groups} />
          </div>
        </div>
      </div>
    </div>
  );
}

function BrandGroups({ brands }: { brands: BrandGroupType[] }) {
  return (
    <div className="flex w-full flex-col justify-center gap-4 md:max-w-[800px] md:flex-row md:gap-6">
      {brands.map((brand, index) => (
        <React.Fragment key={`brand-group-${brand.items[0]?.text}-${index}`}>
          <BrandGroup items={brand.items} />
          <HorizontalLine className="col-span-full md:hidden" />
        </React.Fragment>
      ))}
    </div>
  );
}

function BrandGroup({ items }: { items: BrandGroupType['items'] }) {
  return (
    <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-1 md:gap-5">
      {items.map((item, index) => (
        <Item key={item.text} boldText={index === 0} item={item} />
      ))}
    </div>
  );
}

function Item({
  item,
  boldText
}: {
  boldText?: boolean;
  item: InternalLink | ExternalLink;
}) {
  return (
    <div
      className={`bg-grey-tile p-1 text-center ${boldText ? 'font-semibold' : ''}`}
    >
      {item.type === 'internal' ? (
        <Tracked.InternalLink
          variant="none"
          to={item.path}
          name="TOP_CATEGORY"
          pagePosition="Homepage Top Categories"
        >
          {item.text}
        </Tracked.InternalLink>
      ) : (
        <Tracked.ExternalLink
          variant="none"
          href={item.url}
          name="TOP_CATEGORY"
          pagePosition="Homepage Top Categories"
        >
          {item.text}
        </Tracked.ExternalLink>
      )}
    </div>
  );
}
